/**
 * MountRequestedDialog — the confirmation a recipient sees AFTER clicking
 * "Add to my Drive" on a share link. Replaces the easy-to-miss inline banner
 * with a focused modal that spells out what just happened and what to expect.
 *
 * Three outcomes:
 *   - pending   → the owner/admin must approve. The recipient gets a
 *                 notification when it lands in their Drive.
 *   - established → the recipient was already connected to the owner, so the
 *                 mount materialized instantly. Offer a direct "Open in Drive".
 *   - no_access → it landed, but as the organization's EXISTING shared folder,
 *                 which this person can't open yet (#773) — its managers were
 *                 asked. No "Open in Drive": there is nothing they can open.
 *
 * Rendered outside the workspace ThemedArea (the share-view chrome lives at
 * /share-view/*), so the theme class is applied explicitly — same rule the
 * sibling AuthPromptModal follows.
 */
import { useEffect } from 'react'
import { CheckCircle2, Clock, ExternalLink, X } from 'lucide-react'

interface Props {
  outcome: 'pending' | 'established' | 'no_access'
  themeClass: string
  /** The shared folder/file name, for the body copy. */
  rootName: string | null
  /** The owner's org/tenant name ("from <Owner>"), when known. */
  ownerName: string | null
  /** Present for the established outcome — links straight to the new mount. */
  driveUrl?: string
  onClose: () => void
}

export function MountRequestedDialog({ outcome, themeClass, rootName, ownerName, driveUrl, onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const target = rootName ? <span className="font-medium themed-text">“{rootName}”</span> : 'this shared folder'
  const fromOwner = ownerName ? <> from <span className="font-medium text-blue-300">{ownerName}</span></> : null

  const pending = outcome === 'pending'
  const noAccess = outcome === 'no_access'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} themed-bg-secondary themed-text border themed-border rounded-lg shadow-2xl w-full max-w-md p-6 relative`}>
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 themed-text-muted hover:themed-text"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className={`mb-3 flex h-12 w-12 items-center justify-center rounded-full ${pending ? 'bg-blue-500/15' : 'bg-emerald-500/15'}`}>
            {pending
              ? <Clock size={24} className="text-blue-400" />
              : <CheckCircle2 size={24} className="text-emerald-400" />}
          </div>

          <h2 className="text-base font-semibold themed-text mb-2">
            {pending ? 'Request sent' : noAccess ? 'Already in your organization' : 'Added to your Drive'}
          </h2>

          <p className="text-sm themed-text-secondary leading-relaxed mb-1">
            {pending ? (
              <>
                We asked the owner{fromOwner} to add {target} to your Drive.
              </>
            ) : noAccess ? (
              <>
                Your organization already has {target}{fromOwner}, and you don’t have access to it
                yet. Someone who manages it has been asked to add you.
              </>
            ) : (
              <>
                {target}{fromOwner} is now in your Drive.
              </>
            )}
          </p>
          {pending && (
            <p className="text-sm themed-text-muted leading-relaxed">
              An admin needs to approve it first. You’ll get a notification the
              moment it’s ready — then it shows up in your Drive automatically.
            </p>
          )}
        </div>

        <div className="mt-5 flex items-center justify-center gap-2">
          {!pending && !noAccess && driveUrl && (
            <a
              href={driveUrl}
              className="themed-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium"
            >
              Open in my Drive
              <ExternalLink size={14} />
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            className={`${!pending && !noAccess && driveUrl ? 'themed-btn-secondary' : 'themed-btn-primary'} px-4 py-2 rounded-md text-sm font-medium`}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  )
}
